import React from 'react';
import { useFormik } from 'formik';
import { InfoIcon, UploadImageIcon } from '../../svg';
import useDeviceDetect from '../../helpers/screens';
import { Button } from '../common/Button';
import Input from '../common/Input';
import { SelectedFile } from '../common/cards/EditLinkandAppearance';
import ToolTip from '../common/ToolTip';
import useI18n from '../../hooks/useI18n';

interface EditAppearanceFormData {
  title: string;
  description: string;
  file: File | null;
}

interface EditAppearanceProps {
  fileInputRef: React.RefObject<HTMLInputElement>;
  formik: ReturnType<typeof useFormik<EditAppearanceFormData>>;
  selectedFile: SelectedFile | null;
  imageUrl: string | null;
  isUploading: boolean;
  hasFileChanged: boolean;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => Promise<void>;
  onImportClick: () => void;
}

export default function EditAppearance({
  fileInputRef,
  formik,
  selectedFile,
  isUploading,
  onFileSelect,
  onUpload,
  onImportClick,
}: EditAppearanceProps) {
  const { t } = useI18n();
  const { isMobile } = useDeviceDetect();

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderUploadSection = () => {
    if (selectedFile) {
      return (
        <div className="flex lg:items-center justify-between flex-col lg:flex-row space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16">
              <img
                src={selectedFile.preview}
                alt="Preview"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
            <div>
              <p className="text-base font-medium">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>
          <div className="flex justify-center" style={{ marginTop: 0 }}>
            <Button onClick={onUpload} disabled={isUploading} className="w-fit">
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col lg:flex-row items-center justify-center text-center lg:text-left lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
        <div className="flex lg:flex-row items-center lg:space-x-4">
          {!isMobile && (
            <div>
              <UploadImageIcon className="mx-auto lg:mx-0" />
            </div>
          )}
          <div>
            <p className="text-sm text-black">
              Select a file or drag and drop here
            </p>
            <p className="text-sm text-black text-opacity-40">
              PNG & JPG file size no more than 5MB
            </p>
            {formik.errors.file && (
              <div className="text-red-500 text-xs">{formik.errors.file}</div>
            )}
          </div>
        </div>
        <Button onClick={onImportClick}>{t('components.import')}</Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      <div className="border border-dashed border-black border-opacity-25 rounded-[24px] px-4 py-4 lg:py-4">
        {renderUploadSection()}
        <input
          id="file-upload"
          name="file-upload"
          type="file"
          ref={fileInputRef}
          className="sr-only"
          onChange={onFileSelect}
          accept=".png,.jpg,.jpeg,.svg"
        />
      </div>

      {/* Title and Description Inputs */}
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <div className="relative">
          <Input
            id="title"
            placeholder="Title"
            type="text"
            {...formik.getFieldProps('title')}
            aria-label="Title"
            className={`text-[#4D4D4D] pr-12 ${formik.errors.title ? '!border-red-500' : ''}`}
          />

          <div className="absolute right-3 top-3">
            <ToolTip text="Maximum 70 characters">
              <InfoIcon className="w-5 h-5 cursor-pointer text-[#94A3B8]" />
            </ToolTip>
          </div>
          {formik.errors.title && (
            <div className="text-red-500 text-xs !mt-[2px]">
              {formik.errors.title}
            </div>
          )}
        </div>

        <div className="relative">
          <textarea
            id="description"
            placeholder="Description"
            {...formik.getFieldProps('description')}
            aria-label="Description"
            rows={3}
            className={`w-full h-[50px] md:h-full rounded-3xl border border-[#B3B3B2] disabled:border-[#B3B3B2]  disabled:bg-[#F4F4F4] disabled:text-[#B3B3B2] focus:border focus:border-[#113E53] outline-none resize-none py-3 px-4 pr-12 text-[#4D4D4D] ${formik.errors.description ? '!border-red-500' : ''}`}
          />
          <div className="absolute right-3 top-3">
            <ToolTip text="Maximum 200 words">
              <InfoIcon className="w-5 h-5 cursor-pointer text-[#94A3B8]" />
            </ToolTip>
          </div>
        </div>
        {formik.errors.description && (
          <div className="text-red-500 text-xs !mt-[2px]">
            {formik.errors.description}
          </div>
        )}
      </form>
    </div>
  );
}
